import { Fragment, useCallback, useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { ChevronDown, ChevronRight, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "@/hooks/use-toast";
import {
  getDeadLetters,
  replayDeadLetter,
  discardDeadLetter,
  DeadLetterItem,
  DeadLetterQueueKey,
} from "@/services/workerApi";

const QUEUE_LABELS: Record<DeadLetterQueueKey, string> = {
  enrichment: "Enrich. candidats",
  company_enrichment: "Enrich. entreprises",
  school_enrichment: "Enrich. écoles",
  company_jobs: "Offres d'emploi",
};

function formatAge(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  if (d > 0) return `${d}j ${Math.floor((seconds % 86400) / 3600)}h`;
  const h = Math.floor(seconds / 3600);
  if (h > 0) return `${h}h ${Math.floor((seconds % 3600) / 60)}m`;
  const m = Math.floor(seconds / 60);
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

// Suivi des tâches écartées (dead-letter) : tâches sorties de la file active
// après N échecs. On peut les RE-JOUER (remises en file + compteur d'échecs
// remis à zéro) ou les ABANDONNER. Surface le ZSET dead-letter écrit par le
// back (push_to_dead_letter) qui n'était jusqu'ici lu par personne.
export function DeadLettersSection({ navigate }: { navigate: NavigateFunction }) {
  const [items, setItems] = useState<DeadLetterItem[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // member en cours d'action
  const [expanded, setExpanded] = useState<Set<string>>(new Set()); // payloads dépliés

  function toggleExpanded(member: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(member)) next.delete(member);
      else next.add(member);
      return next;
    });
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getDeadLetters(navigate, { limit: 200 });
      setItems(res.items);
      setTotals(res.totals);
    } catch {
      setError(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onReplay(it: DeadLetterItem) {
    setBusy(it.member);
    try {
      const res = await replayDeadLetter(navigate, { queue: it.queue, member: it.member });
      if (res.replayed) {
        toast({
          title: "Tâche rejouée",
          description: res.requeued
            ? `Remise en file (${QUEUE_LABELS[it.queue]}).`
            : "Déjà présente en file (dédupliquée).",
        });
        setItems((prev) => prev.filter((x) => x.member !== it.member));
      } else {
        toast({ title: "Rien à rejouer", description: res.reason ?? "Tâche introuvable." });
        setItems((prev) => prev.filter((x) => x.member !== it.member));
      }
    } catch (e) {
      toast({ variant: "destructive", title: "Échec du rejeu", description: (e as Error)?.message ?? "Erreur" });
    } finally {
      setBusy(null);
    }
  }

  async function onDiscard(it: DeadLetterItem) {
    const ok = await confirmDialog({
      title: "Abandonner définitivement",
      description: `Retire cette tâche écartée (${QUEUE_LABELS[it.queue]}, entité ${it.entity_id ?? "?"}) sans la rejouer.`,
      confirmLabel: "Abandonner",
    });
    if (!ok) return;
    setBusy(it.member);
    try {
      await discardDeadLetter(navigate, { queue: it.queue, member: it.member });
      toast({ title: "Tâche abandonnée" });
      setItems((prev) => prev.filter((x) => x.member !== it.member));
    } catch (e) {
      toast({ variant: "destructive", title: "Échec", description: (e as Error)?.message ?? "Erreur" });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-sm font-medium">Tâches écartées (dead-letter)</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Tâches sorties de la file après N échecs — à rejouer ou abandonner.
          </p>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={load} title="Rafraîchir">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(QUEUE_LABELS) as DeadLetterQueueKey[]).map((q) => (
            <Badge key={q} variant={totals[q] ? "destructive" : "secondary"}>
              {QUEUE_LABELS[q]} : {totals[q] ?? 0}
            </Badge>
          ))}
        </div>

        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Chargement...</div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-destructive">
            Erreur de chargement — cliquer sur ⟳ pour réessayer
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Aucune tâche écartée 🎉
          </div>
        ) : (
          <div className="max-h-[420px] overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">File</TableHead>
                  <TableHead className="w-[90px]">Entité</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="w-[90px]">Âge</TableHead>
                  <TableHead className="w-[150px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <Fragment key={it.member}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => toggleExpanded(it.member)}
                      title="Cliquer pour voir le payload complet"
                    >
                      <TableCell className="text-xs">
                        <span className="inline-flex items-center gap-1">
                          {expanded.has(it.member) ? (
                            <ChevronDown className="h-3 w-3 shrink-0" />
                          ) : (
                            <ChevronRight className="h-3 w-3 shrink-0" />
                          )}
                          {QUEUE_LABELS[it.queue]}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{it.entity_id ?? "—"}</TableCell>
                      <TableCell className="max-w-[280px] truncate text-xs" title={it.reason ?? ""}>
                        {it.reason ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">{formatAge(it.age_seconds)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            disabled={busy !== null}
                            onClick={() => onReplay(it)}
                            title="Rejouer (remettre en file)"
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            Rejouer
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            disabled={busy !== null}
                            onClick={() => onDiscard(it)}
                            title="Abandonner définitivement"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded.has(it.member) && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={5} className="p-0">
                          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words p-3 text-[11px] leading-snug">
                            {it.payload
                              ? JSON.stringify(it.payload, null, 2)
                              : it.member}
                          </pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
